"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Null = exports.Primitives = exports.FromJsConstructor = exports.FromJsConstructorBuilder = void 0;
const IonTypes_1 = require("../IonTypes");
const util_1 = require("../util");
const Value_1 = require("./Value");
// Converts the provided Iterable into a Set. If no iterable is provided, returns an empty set.
function _newSet(values) {
    if ((0, util_1._hasValue)(values)) {
        return new Set(values);
    }
    return new Set();
}
/**
 * Builder to configure and instantiate FromJsConstructor objects. See the documentation for
 * the FromJsConstructor class for a description of each field.
 *
 * Package-visible for use in dom.Value subclass definitions.
 * @private
 */
class FromJsConstructorBuilder {
    constructor() {
        this._primitives = _newSet();
        this._classesToUnbox = _newSet();
        this._classes = _newSet();
    }
    withPrimitives(...primitives) {
        this._primitives = _newSet(primitives);
        return this;
    }
    withClasses(...classes) {
        this._classes = _newSet(classes);
        return this;
    }
    withClassesToUnbox(...classes) {
        this._classesToUnbox = _newSet(classes);
        return this;
    }
    build() {
        return new FromJsConstructor(this._primitives, this._classesToUnbox, this._classes);
    }
}
exports.FromJsConstructorBuilder = FromJsConstructorBuilder;
/**
 * Provides common conversion and validation logic needed to instantiate subclasses of dom.Value
 * from a given Javascript value of unknown type (`any`) and an optional annotations array.
 *
 * Given a Javascript value, will test its type to see whether it is:
 * 1. A primitive that is supported by the specified constructor.
 * 2. A boxed primitive that is supported by the specified constructor if unboxed via `valueOf()`.
 * 3. A class that is supported by the specified constructor as-is, including boxed primitives.
 *
 * If the value matches any of the above descriptions, the provided constructor will be invoked
 * with the value; otherwise, throws an Error.
 *
 * Constructors are expected to be compatible with the signature:
 *
 *      constructor(value, annotations: string[]): ClassName
 *
 * See also: Value._fromJsValue()
 */
class FromJsConstructor {
    /**
     * Constructor.
     *
     * @param _primitives       Primitive types that will be passed through as-is.
     * @param _classesToUnbox   Boxed primitive types that will be converted to primitives via
     *                          `valueOf()` and then passed through.
     * @param _classes          Classes that will be passed through as-is.
     */
    constructor(_primitives, _classesToUnbox, _classes) {
        this._primitives = _primitives;
        this._classesToUnbox = _classesToUnbox;
        this._classes = _classes;
    }
    /**
     * Invokes the provided constructor if `jsValue` is of a supported data type; otherwise
     * throws an Error.
     *
     * @param constructor   A dom.Value subclass's constructor to call.
     * @param jsValue       A Javascript value to validate/unbox before passing through to
     *                      the constructor.
     * @param annotations   An optional array of strings to associate with the newly constructed
     *                      dom.Value.
     */
    construct(constructor, jsValue, annotations = []) {
        if (jsValue === null) {
            return new Null(IonTypes_1.IonTypes.NULL, annotations);
        }
        const jsValueType = typeof jsValue;
        if (jsValueType === "object") {
            // jsValue is an unsupported boxed primitive, but we can use it if we convert it to a primitive first
            if (this._classesToUnbox.has(jsValue.constructor)) {
                return new constructor(jsValue.valueOf(), annotations);
            }
            // jsValue is an Object of a supported type, including boxed primitives
            if (this._classes.has(jsValue.constructor)) {
                return new constructor(jsValue, annotations);
            }
            throw new Error(`Unable to construct a(n) ${constructor.name} from a ${jsValue.constructor.name}.`);
        }
        if (this._primitives.has(jsValueType)) {
            return new constructor(jsValue, annotations);
        }
        throw new Error(`Unable to construct a(n) ${constructor.name} from a ${jsValueType}.`);
    }
}
exports.FromJsConstructor = FromJsConstructor;
// This namespace will be merged with the class definition above. This allows static values to invoke functions in the
// FromJsConstructor class after the class has been fully initialized.
(function (FromJsConstructor) {
    // Useful for dom.Value subclasses that do not use a FromJsConstructor (e.g. Struct, List).
    // Because it has no supported types, any attempt to use this instance will throw an Error.
    FromJsConstructor.NONE = new FromJsConstructorBuilder().build();
})(FromJsConstructor = exports.FromJsConstructor || (exports.FromJsConstructor = {}));
/**
 * A mapping of primitive types to the corresponding string that will be returned by
 * the `typeof` operator.
 */
exports.Primitives = {
    /*
     * Some possible values are not included because they are unsupported. In particular:
     *   - "object" indicates that a value is not a primitive.
     *   - The mapping from Javascript's "symbol" to Ion's type system is not yet clear.
     *   - No constructors accept "undefined" as a parameter.
     */
    Boolean: "boolean",
    Number: "number",
    String: "string",
};
/**
 * Represents a null[1] value in an Ion stream.
 *
 * An Ion null differs from a Javascript null in that an instance of an Ion null may have data associated with it.
 *
 * In particular, Ion nulls have an Ion data type:
 *
 *      text encoding | Ion data type
 *  ======================================
 *      null          | IonType.NULL
 *      null.null     | IonType.NULL
 *      null.string   | IonType.STRING
 *      null.int      | IonType.INT
 *      null.struct   | IonType.STRUCT
 *      ...
 *
 * They can also be annotated:
 *
 *      customerName::null.string
 *      dollars::null.decimal
 *      meters::null.float
 *
 * [1] http://amzn.github.io/ion-docs/docs/spec.html#null
 */
class Null extends (0, Value_1.Value)(Object, IonTypes_1.IonTypes.NULL, FromJsConstructor.NONE) {
    static _operationIsSupported(ionType, operation) {
        return Null._supportedIonTypesByOperation.get(operation).has(ionType);
    }
    /**
     * Constructor.
     * @param ionType       The Ion data type associated with this null value.
     * @param annotations   An optional array of strings to associate with this null value.
     */
    constructor(ionType = IonTypes_1.IonTypes.NULL, annotations = []) {
        super();
        this._ionType = ionType;
        this._setAnnotations(annotations);
    }
    isNull() {
        return true;
    }
    // If a [type]Value() operation was called on this Null value, we need to see whether the associated Ion type
    // supports that conversion. If it does, we'll return a JS null. If it doesn't, we'll throw an Error.
    _convertToJsNull(operation) {
        if (Null._operationIsSupported(this.getType(), operation)) {
            return null;
        }
        throw new Error(`${operation}() is not supported by Ion type ${this.getType().name}`);
    }
    // If this Null's Ion type supports the requested operation, throw an Error indicating this was a null dereference.
    // Otherwise, throw an Error indicating that the requested operation is not supported.
    _unsupportedOperationOrNullDereference(operation) {
        if (Null._operationIsSupported(this.getType(), operation)) {
            throw new Error(`${operation}() called on a null ${this.getType().name}.`);
        }
        throw new Error(`${operation}() is not supported by Ion type ${this.getType().name}`);
    }
    booleanValue() {
        return this._convertToJsNull("booleanValue");
    }
    numberValue() {
        return this._convertToJsNull("numberValue");
    }
    bigIntValue() {
        return this._convertToJsNull("bigIntValue");
    }
    decimalValue() {
        return this._convertToJsNull("decimalValue");
    }
    stringValue() {
        return this._convertToJsNull("stringValue");
    }
    dateValue() {
        return this._convertToJsNull("dateValue");
    }
    uInt8ArrayValue() {
        return this._convertToJsNull("uInt8ArrayValue");
    }
    fieldNames() {
        this._unsupportedOperationOrNullDereference("fieldNames");
    }
    fields() {
        this._unsupportedOperationOrNullDereference("fields");
    }
    elements() {
        this._unsupportedOperationOrNullDereference("elements");
    }
    get(...pathElements) {
        return null;
    }
    toString() {
        if (this.getType() == IonTypes_1.IonTypes.NULL) {
            return "null";
        }
        return "null." + this._ionType.name;
    }
    /**
     * Converts this dom.Null to a Javascript null when being serialized with `JSON.stringify()`.
     */
    toJSON() {
        return null;
    }
    writeTo(writer) {
        writer.setAnnotations(this.getAnnotations());
        writer.writeNull(this.getType());
    }
    _valueEquals(other, options = {
        epsilon: null,
        ignoreAnnotations: false,
        ignoreTimestampPrecision: false,
        onlyCompareIon: true,
    }) {
        let isSupportedType = false;
        let valueToCompare = null;
        // if the provided value is an ion.dom.Null instance.
        if (other instanceof Null) {
            isSupportedType = true;
            valueToCompare = other;
        }
        else if (!options.onlyCompareIon) {
            // We will consider other Null-ish types
            if (other === null && this._ionType.name === "null") {
                return true;
            }
        }
        if (!isSupportedType) {
            return false;
        }
        return this._ionType.name === valueToCompare._ionType.name;
    }
}
exports.Null = Null;
Null._supportedIonTypesByOperation = new Map([
    ["booleanValue", new Set([IonTypes_1.IonTypes.BOOL])],
    ["numberValue", new Set([IonTypes_1.IonTypes.INT, IonTypes_1.IonTypes.FLOAT, IonTypes_1.IonTypes.DECIMAL])],
    ["bigIntValue", new Set([IonTypes_1.IonTypes.INT])],
    ["decimalValue", new Set([IonTypes_1.IonTypes.DECIMAL])],
    ["stringValue", new Set([IonTypes_1.IonTypes.STRING, IonTypes_1.IonTypes.SYMBOL])],
    ["dateValue", new Set([IonTypes_1.IonTypes.TIMESTAMP])],
    ["timestampValue", new Set([IonTypes_1.IonTypes.TIMESTAMP])],
    ["uInt8ArrayValue", new Set([IonTypes_1.IonTypes.BLOB, IonTypes_1.IonTypes.CLOB])],
    ["fields", new Set([IonTypes_1.IonTypes.STRUCT])],
    ["fieldNames", new Set([IonTypes_1.IonTypes.STRUCT])],
    ["elements", new Set([IonTypes_1.IonTypes.LIST, IonTypes_1.IonTypes.SEXP, IonTypes_1.IonTypes.STRUCT])],
]);
